import { brand } from 'config';
import { DEFAULT_DELIVERY_MESSAGES } from '../../../constants/common';
import { isEmpty } from '~/utils/commonUtils';

/**
 * @param {String} availabilityMessage
 * @returns {import('../types').DeliveryType}'
 */
const getDefaultPikcupDeliveryType = (availabilityMessage) => ({
  deliveryCode: 'S2S',
  deliveryMessage: availabilityMessage || '',
  tierLevel: 'Default',
  arrivesByDate: '',
});

/**
 *
 * @param {import('../types').UpcNumberBopsMap} selectedBopsData
 * @param {import('@nuxt-common/media-card-large/app/composables/useUpc/types').UseUpc.Upc} selectedUpcData
 * @param {boolean} marketplaceFlag
 * @returns {import('../types').Pickup}
 */
function generatePickupData(selectedBopsData, selectedUpcData, marketplaceFlag = false) {
  const {
    availability = {
      lowAvailability: false,
      bopsAvailability: false,
      bossAvailability: false,
      isBossUpc: false,
      availabilityMessage: '',
    },
    deliveryTypes = [],
  } = selectedBopsData;
  const { availability: upcAvailability = { orderType: '' } } = selectedUpcData;

  const pickupDeliveryType =
    deliveryTypes.find((type) => type.deliveryCode === 'S2S') ||
    (!isEmpty(selectedBopsData) && availability?.bossAvailability && getDefaultPikcupDeliveryType(availability.availabilityMessage));

  return {
    ...availability,
    orderType: upcAvailability?.orderType,
    bossAvailabilityMessage: availability.availabilityMessage,
    marketplaceItemFlag: marketplaceFlag,
    ...(pickupDeliveryType && { deliveryType: pickupDeliveryType }),
  };
}

/**
 * @param {String} orderType
 * @returns {import('../types').DeliveryType}'
 */
const getDefaultShippingDeliveryType = (orderType) => {
  const is1P = ['STR1', 'POOL'].includes(orderType);
  const key = is1P ? `1P_${brand.toUpperCase()}` : orderType;

  return {
    deliveryCode: 'G',
    deliveryMessage: DEFAULT_DELIVERY_MESSAGES[key] || '',
    tierLevel: 'Default',
    arrivesByDate: '',
  };
};

/**
 * Returns the BOPS and SDD availability records for a UPC using the same
 * response maps consumed by the fulfillment product-info parser.
 *
 * @param {import('../types').bopsAvailabilityXapiResponse} availabilityData
 * @param {string|number} selectedUpcNumber
 */
export const getSelectedUpcFulfillmentData = (availabilityData = {}, selectedUpcNumber = '') => ({
  selectedBopsData: availabilityData.upcNumberBopsMap?.[selectedUpcNumber] || availabilityData.upcNumberBopsMap?.[+selectedUpcNumber] || {},
  selectedSddData: availabilityData.upcNumberSddMap?.[selectedUpcNumber] || availabilityData.upcNumberSddMap?.[+selectedUpcNumber] || {},
});

/**
 * @param {import('../types').UpcNumberSddMap} selectedSddData
 * @param {import('@nuxt-common/media-card-large/app/composables/useUpc/types').UseUpc.Upc} selectedUpcData
 * @param {import('../types').UpcNumberBopsMap} selectedBopsData
 * @param {string} zipCode
 * @param {boolean} isBopsUpcAvailabilityUpdateEnabled
 * @param {boolean} [isFulfillmentDrivenAvailabilityEnabled]
 * @returns {import('../types').Ship}
 */
function generateShipData(
  selectedSddData,
  selectedUpcData,
  selectedBopsData,
  zipCode,
  isBopsUpcAvailabilityUpdateEnabled,
  isFulfillmentDrivenAvailabilityEnabled = false,
) {
  const {
    availability = {
      available: false,
      lowAvailability: false,
      availabilityMessage: '',
      message: '',
      orderType: '',
    },
    marketplaceFlag = false,
  } = selectedUpcData;
  const { sddAvailability = false } = selectedSddData;
  const { deliveryTypes = [], campaignInfo = { excluded: true } } = selectedBopsData;

  const isShippableToHome =
    isBopsUpcAvailabilityUpdateEnabled && !isEmpty(selectedBopsData)
      ? Array.isArray(deliveryTypes) && deliveryTypes.some((type) => type.deliveryCode === 'G')
      : availability?.available;

  const shippingDeliveryType = zipCode
    ? deliveryTypes.find((type) => type.deliveryCode === 'G') ||
      (!isEmpty(selectedBopsData) &&
        (!isBopsUpcAvailabilityUpdateEnabled || isShippableToHome) &&
        getDefaultShippingDeliveryType(availability.orderType))
    : undefined;

  return {
    ...availability,
    ...(isFulfillmentDrivenAvailabilityEnabled && { available: isShippableToHome }),
    sddAvailability,
    marketplaceItemFlag: marketplaceFlag,
    sellerName: selectedUpcData?.seller?.sellerName || '',
    ...(shippingDeliveryType && { deliveryType: shippingDeliveryType }),
    campaignInfo,
  };
}

/**
 * @param {{
 *  selectedRadioButton: string,
 *  availabilityData: import('../types').bopsAvailabilityXapiResponce
 *  selectedUpcId?: string,
 *  upcData: import('@nuxt-common/media-card-large/app/composables/useUpc/types').UseUpc.UpcData
 *  product: Product | undefined,
 *  zipCode?: string,
 *  isBopsUpcAvailabilityUpdateEnabled?: boolean,
 *  isFulfillmentDrivenAvailabilityEnabled?: boolean,
 *  isSiblingUpc?: boolean,
 * }} param0
 * @returns {import('../types').ProductInfo}
 */
export const productInfoParser = ({
  selectedRadioButton = 'ShipIt',
  availabilityData,
  selectedUpcId = '',
  upcData,
  product,
  zipCode,
  isBopsUpcAvailabilityUpdateEnabled = false,
  isFulfillmentDrivenAvailabilityEnabled = false,
  isSiblingUpc = false,
}) => {
  const { upcNumberBopsMap = {}, sddStore: shipFromStore = {}, upcNumberSddMap = {} } = availabilityData;

  const { id = '', detail } = product || {};
  const { deliveryDays } = product?.relationships.upcs?.[selectedUpcId] || {
    deliveryDays: {},
  };
  const selectedUpcData = upcData?.upcs?.[selectedUpcId] || {};
  const { number: selectedUpcNumber, marketplaceFlag } = selectedUpcData;

  const { selectedBopsData, selectedSddData } = getSelectedUpcFulfillmentData({ upcNumberBopsMap, upcNumberSddMap }, selectedUpcNumber);

  const pickup = generatePickupData(selectedBopsData, selectedUpcData, marketplaceFlag);
  const ship = generateShipData(
    selectedSddData,
    selectedUpcData,
    selectedBopsData,
    zipCode,
    isBopsUpcAvailabilityUpdateEnabled,
    isFulfillmentDrivenAvailabilityEnabled,
  );

  if (isSiblingUpc) {
    ship.available = false;
    ship.sddAvailability = false;
    ship.lowAvailability = false;
    ship.deliveryType = undefined;
    pickup.bopsAvailability = false;
    pickup.bossAvailability = false;
    pickup.lowAvailability = false;
    pickup.deliveryType = undefined;
  }

  const sameDayFee = shipFromStore?.sddShippingFee;
  const sameDay = {
    available: ship.sddAvailability || false,
    orderType: ship.orderType || '',
    ...(sameDayFee === null || sameDayFee === undefined ? {} : { sameDayFee }),
    campaignInfo: ship.campaignInfo,
  };

  const upcNumber = selectedUpcData?.number;
  const eligibleForPreOrder = detail?.flags?.eligibleForPreOrder || false;

  return {
    selectedRadioButton,
    selectedUpcId,
    productId: `${id}`,
    productName: detail?.name || '',
    isEGiftCard: false,
    bagItemId: '',
    pickup,
    ship,
    sameDay,
    deliveryDays,
    shipFromStore: {
      ...shipFromStore,
      orderCutoffTime: shipFromStore?.orderCutOffTime || '',
    },
    upcNumber,
    eligibleForPreOrder,
  };
};

export default productInfoParser;
